import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_ENDPOINTS, API_REQUEST_CONFIG, API_BASE_URL } from '../config';

interface User {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  fullName?: string;
  company?: string;
  role?: string;
  companyInfo?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: any) => Promise<void>;
  updateProfile: (data: any) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 检查本地存储中的用户信息和 token
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const loginConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include' as RequestCredentials,
        body: JSON.stringify({ email, password }),
      };
      
      const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, loginConfig);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.msg || '登录失败');
      }

      const responseData = await response.json();

      if (!responseData.ok) {
        throw new Error(responseData.msg || '登录失败');
      }

      const token = responseData.data.access_token;
      const userData = responseData.data.user;

      // 获取完整的用户资料
      const profileConfig = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include' as RequestCredentials,
      };

      const profileResponse = await fetch(API_ENDPOINTS.AUTH.PROFILE.GET, profileConfig);

      if (!profileResponse.ok) {
        throw new Error('获取用户资料失败');
      }

      const profileData = await profileResponse.json();

      // 使用完整的用户资料构建用户信息
      const userInfo: User = {
        id: String(userData.id),
        username: userData.username,
        email: userData.email,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`,
        fullName: profileData.user.full_name || '',
        company: profileData.user.company || '',
        role: profileData.user.role || '',
        companyInfo: profileData.user.company_info || '',
      };
      
      // 持久化存储用户信息和 token
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);
    } catch (error) {
      console.error('登录失败');
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      throw error;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const logoutConfig = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          };
          
          await fetch(API_ENDPOINTS.AUTH.LOGOUT, logoutConfig);
        } catch (error) {
          console.error('登出请求失败');
        }
      }
    } catch (error) {
      console.error('登出失败');
    } finally {
      // 无论登出请求是否成功，都清除本地存储和状态
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  };

  const register = async (data: any) => {
    try {
      // 注册用户
      const registerConfig = {
        ...API_REQUEST_CONFIG,
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          username: data.username,
          password: data.password,
          verification_code: data.verificationCode,
        }),
      };

      const registerResponse = await fetch(API_ENDPOINTS.AUTH.REGISTER, registerConfig);

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json().catch(() => null);
        throw new Error(errorData?.msg || '注册失败');
      }

      const registerData = await registerResponse.json();

      if (!registerData.ok) {
        throw new Error(registerData.msg || '注册失败');
      }

      const token = registerData.data.access_token;
      const userData = registerData.data.user;

      // 完善用户信息
      const updateProfileConfig = {
        ...API_REQUEST_CONFIG,
        method: 'PUT',
        headers: {
          ...API_REQUEST_CONFIG.headers,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          full_name: data.fullName,
          company: data.company,
          role: data.role,
          company_info: data.companyInfo || '',
        }),
      };

      const profileResponse = await fetch(API_ENDPOINTS.AUTH.PROFILE.UPDATE, updateProfileConfig);

      if (!profileResponse.ok) {
        throw new Error('完善用户信息失败');
      }

      const userInfo: User = {
        id: String(userData.id),
        username: userData.username,
        email: userData.email,
        fullName: data.fullName,
        company: data.company,
        role: data.role,
        companyInfo: data.companyInfo,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${data.email}`,
      };
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);
    } catch (error) {
      console.error('注册失败');
      throw error;
    }
  };

  const updateProfile = async (data: any) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('未登录');
      }

      const response = await fetch(API_ENDPOINTS.AUTH.PROFILE.UPDATE, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: data.fullName,
          company: data.company,
          role: data.role,
          company_info: data.companyInfo || '',
        }),
      });

      if (!response.ok) {
        throw new Error('更新个人信息失败');
      }

      const updatedUser: User = {
        ...user!,
        fullName: data.fullName,
        company: data.company,
        role: data.role,
        companyInfo: data.companyInfo,
      };

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const verifyCode = async (email: string, code: string) => {
    try {
      const verifyConfig = {
        ...API_REQUEST_CONFIG,
        method: 'POST',
        body: JSON.stringify({ email, code })
      };
      
      const response = await fetch(API_ENDPOINTS.EMAIL.VERIFY_CODE, verifyConfig);
      
      if (!response.ok) {
        console.error('验证码验证失败');
        return false;
      }
      const data = await response.json();
      return data.ok === true;
    } catch (error) {
      console.error('验证码验证失败');
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, register, updateProfile, verifyCode }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 


